import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS } from '@/components/ui/recipes';

/** A text action's tone: a link (underlined, in the link ink), an action (the brand ink, underlined on hover), a quiet action, one that deletes, one on the hero band (navy into berry), a link-like one on a deep fill (the share pages' Copy link), one on a deep featured card, or a pill that fills on hover (a library's Show more). */
const TONES = {
  link: 'text-lureo-link decoration-lureo-link-decoration underline-offset-lureo-link-underline-offset visited:text-lureo-link-visited hover:text-lureo-link-hover hover:decoration-lureo-link-decoration-hover hover:shadow-lureo-link-hover-wash active:text-lureo-link-pressed',
  action: 'text-lureo-brand-ink underline-offset-2 hover:underline',
  quiet: 'text-lureo-ink-subtle hover:text-lureo-ink',
  destructive: 'text-lureo-danger-ink underline-offset-2 hover:underline',
  hero: 'text-lureo-on-emphasis-subtle hover:text-lureo-on-emphasis',
  'emphasis-link': 'text-[14px] font-medium text-lureo-on-emphasis/72 underline decoration-lureo-on-emphasis/28 underline-offset-4 hover:text-lureo-on-emphasis hover:decoration-lureo-on-emphasis/60', // theme-exempt: the share screens' 14px Copy link, in px like the viewer's
  feature: 'text-lureo-plan-link underline-offset-2 hover:underline',
  pill: 'rounded-lureo-radius-pill px-2.5 py-1.5 gap-1.5 text-lureo-14 font-medium text-lureo-brand-ink hover:bg-lureo-brand-ink/8',
} as const;

const BASE = 'inline-flex items-center gap-1 rounded-lureo-radius-sm font-lureo-font-ui transition-colors [&_svg]:h-3.5 [&_svg]:w-3.5';
const SIZES = { sm: 'text-lureo-12', md: 'text-lureo-13 font-medium' } as const;

interface TextButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: keyof typeof TONES;
  size?: keyof typeof SIZES;
}

/** Lureo's text action: an action that reads as words (Remove, Skip, Learn more), not a button; tone link for one that reads as a link (Forgot password?). */
export const TextButton = React.forwardRef<HTMLButtonElement, TextButtonProps>(
  ({ tone = 'action', size = 'md', className, type = 'button', ...props }, ref) => (
    <button ref={ref} type={type} className={cn(BASE, SIZES[size], TONES[tone], FOCUS, DISABLED, className)} {...props} />
  ),
);
TextButton.displayName = 'TextButton';

interface TextLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  tone?: keyof typeof TONES;
  size?: keyof typeof SIZES;
  /** Draws its child (a router Link) instead of an <a>. */
  asChild?: boolean;
}

/** The same, as a link to another page or site. */
export const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(({ tone = 'link', size = 'md', asChild = false, className, ...props }, ref) => {
  const Tag = asChild ? Slot : 'a';
  return <Tag ref={ref} className={cn(BASE, SIZES[size], TONES[tone], FOCUS, className)} {...props} />;
});
TextLink.displayName = 'TextLink';
